import React, { useState, useEffect } from 'react';
import { Calendar, Check, X, Sparkles } from 'lucide-react';
import api from '../../../services/api';
import RequestHistoryModal from '../../../components/RequestHistoryModal';
import RequestDetailsModal from '../../../components/RequestDetailsModal';
import HybridScheduleModal from '../../../components/HybridScheduleModal';
import BulkGrantModal from '../../../components/BulkGrantModal';

const TeamRequests = () => {
  const [activeTab, setActiveTab] = useState('team'); // 'team', 'leaves' or 'wfh'
  const [teamMembers, setTeamMembers] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [wfhRequests, setWfhRequests] = useState([]);
  const [odRequests, setOdRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [currentHistory, setCurrentHistory] = useState([]);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [hybridModalOpen, setHybridModalOpen] = useState(false);
  const [selectedHybridMember, setSelectedHybridMember] = useState(null);
  const [error, setError] = useState('');

  // Bulk approval state for manager's incoming team requests
  const [selectedTeamReqIds, setSelectedTeamReqIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Allocate WFH/Leave state for manager's team
  const [bulkGrantModalOpen, setBulkGrantModalOpen] = useState(false);
  const [bulkGrantType, setBulkGrantType] = useState('WFH');
  const [grantTargetEmployees, setGrantTargetEmployees] = useState(null);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const [teamRes, leavesRes, wfhRes, odRes] = await Promise.all([
        api.get('/employees/team'),
        api.get('/leaves/team'),
        api.get('/attendance/wfh/team'),
        api.get('/attendance/od/team')
      ]);
      setTeamMembers(teamRes.data);
      setLeaveRequests(leavesRes.data);
      setWfhRequests(wfhRes.data);
      setOdRequests(odRes.data);
    } catch (err) {
      console.error('Failed to fetch team requests', err);
      setError('Failed to load team requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamData();
  }, []);

  // Clear selections when tab changes
  useEffect(() => {
    setSelectedTeamReqIds([]);
  }, [activeTab]);

  const handleUpdateStatus = async (type, id, status) => {
    if (window.confirm(`Are you sure you want to ${status.toLowerCase()} this request? This is final.`)) {
      try {
        let url = '';
        if (type === 'leaves' || type === 'leave') url = `/leaves/${id}/status`;
        else if (type === 'wfh') url = `/attendance/wfh/${id}/status`;
        else if (type === 'od') url = `/attendance/od/${id}/status`;
        
        await api.put(url, { status });
        fetchTeamData();
      } catch (err) {
        alert(err.response?.data?.message || `Failed to update ${type} request`);
      }
    }
  };

  const handleBulkUpdateTeamRequests = async (status) => {
    if (selectedTeamReqIds.length === 0) {
      alert(`Please select at least one ${activeTab} request.`);
      return;
    }
    if (!window.confirm(`Are you sure you want to ${status.toLowerCase()} ${selectedTeamReqIds.length} request(s)?`)) {
      return;
    }

    setBulkProcessing(true);
    try {
      let url = '';
      if (activeTab === 'leaves') url = '/leaves/bulk-status';
      else if (activeTab === 'wfh') url = '/attendance/wfh/bulk-status';

      if (url) {
        await api.put(url, {
          requestIds: selectedTeamReqIds,
          status
        });
      } else {
        // Fallback for OD requests
        for (const id of selectedTeamReqIds) {
          await api.put(`/attendance/od/${id}/status`, { status });
        }
      }

      setSelectedTeamReqIds([]);
      await fetchTeamData();
      alert(`Successfully ${status.toLowerCase()}ed ${selectedTeamReqIds.length} request(s)!`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update selected requests');
    } finally {
      setBulkProcessing(false);
    }
  };

  const getStatusBadge = (req) => {
    // If manager made a decision, that's final. If not, maybe HR did?
    const currentStatus = req.managerStatus !== 'PENDING' ? req.managerStatus : (req.hrStatus !== 'PENDING' ? req.hrStatus : req.status);
    
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
        ${currentStatus === 'APPROVED' ? 'bg-green-100 text-green-800' : 
          currentStatus === 'REJECTED' ? 'bg-red-100 text-red-800' : 
          'bg-yellow-100 text-yellow-800'}`}>
        {currentStatus}
      </span>
    );
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">My Team's Requests</h2>
          <p className="text-sm text-gray-500 mt-1">Review and manage Leave, WFH, and OD/Meeting requests from your direct reports.</p>
        </div>

        {/* Allocate WFH / Leave for Team Members */}
        {teamMembers.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setGrantTargetEmployees(teamMembers);
              setBulkGrantType(activeTab === 'leaves' ? 'LEAVE' : 'WFH');
              setBulkGrantModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all hover:shadow-md shrink-0 w-full md:w-auto justify-center"
          >
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span>Allocate WFH / Leave for Team</span>
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-xl border border-red-100">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <RequestHistoryModal 
          isOpen={historyModalOpen} 
          onClose={() => setHistoryModalOpen(false)} 
          history={currentHistory} 
        />
        <RequestDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          request={selectedRequest}
          type={activeTab === 'leaves' ? 'LEAVE' : activeTab === 'wfh' ? 'WFH' : 'OD'}
        />
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('team')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors mr-8
                ${activeTab === 'team' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              My Team ({teamMembers.length})
            </button>
            <button
              onClick={() => setActiveTab('leaves')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors mr-8
                ${activeTab === 'leaves' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              Leave Requests ({leaveRequests.filter(r => r.managerStatus === 'PENDING').length} Pending)
            </button>
            <button
              onClick={() => setActiveTab('wfh')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors mr-8
                ${activeTab === 'wfh' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              WFH Requests ({wfhRequests.filter(r => r.managerStatus === 'PENDING').length} Pending)
            </button>
            <button
              onClick={() => setActiveTab('od')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === 'od' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              OD & Meeting Requests ({odRequests.filter(r => r.managerStatus === 'PENDING').length} Pending)
            </button>
          </nav>
        </div>

        <div className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading requests...</div>
          ) : activeTab === 'team' ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Today's Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamMembers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">
                      You have no direct reports.
                    </td>
                  </tr>
                ) : (
                  teamMembers.map((member) => (
                    <tr key={member._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{member.firstName} {member.lastName}</div>
                        <div className="text-xs text-gray-500">{member.employeeId} • {member.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {member.department ? member.department.name : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2.5 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                          member.workLocation === 'HYBRID' 
                            ? 'bg-purple-100 text-purple-800 border border-purple-200' 
                            : member.workLocation === 'WFH' 
                              ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                              : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                        }`}>
                          {member.workLocation === 'HYBRID' ? 'Hybrid' : member.workLocation === 'WFH' ? 'Remote' : 'Office'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {member.todayAttendance ? (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${['PRESENT', 'WFH'].includes(member.todayAttendance.status) ? 'bg-green-100 text-green-800' : 
                              member.todayAttendance.status === 'HALF_DAY' ? 'bg-yellow-100 text-yellow-800' : 
                              member.todayAttendance.status === 'CLOCKED_IN' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'}`}>
                            {member.todayAttendance.status.replace(/_/g, ' ')}
                            {member.todayAttendance.status === 'CLOCKED_IN' && ` (In at ${new Date(member.todayAttendance.clockIn).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})})`}
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            ABSENT / NOT PUNCHED
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setGrantTargetEmployees([member]);
                              setBulkGrantType('WFH');
                              setBulkGrantModalOpen(true);
                            }}
                            className="px-2.5 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors inline-flex items-center gap-1 border border-blue-200 shadow-2xs"
                            title="Allocate WFH or Leave to this employee"
                          >
                            <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                            Allocate
                          </button>

                          {member.workLocation === 'HYBRID' && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedHybridMember(member);
                                setHybridModalOpen(true);
                              }}
                              className="px-2.5 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors inline-flex items-center gap-1 border border-purple-200 shadow-2xs"
                              title="Pre-define WFH / Office days"
                            >
                              <Calendar className="w-3.5 h-3.5 text-purple-600" />
                              Hybrid Schedule
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <div>
              <div className="p-3 bg-gradient-to-r from-blue-50/70 to-indigo-50/70 border-b border-blue-100 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-blue-950 uppercase">
                    Team {activeTab} Actions:
                  </span>
                  <span className="text-xs text-blue-700">
                    {selectedTeamReqIds.length} of {(activeTab === 'leaves' ? leaveRequests : activeTab === 'wfh' ? wfhRequests : odRequests).filter(r => r.managerStatus === 'PENDING').length} pending selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={bulkProcessing || selectedTeamReqIds.length === 0}
                    onClick={() => handleBulkUpdateTeamRequests('APPROVED')}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>Approve Selected ({selectedTeamReqIds.length})</span>
                  </button>
                  <button
                    type="button"
                    disabled={bulkProcessing || selectedTeamReqIds.length === 0}
                    onClick={() => handleBulkUpdateTeamRequests('REJECTED')}
                    className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                    <span>Reject Selected ({selectedTeamReqIds.length})</span>
                  </button>
                </div>
              </div>

              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-center w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all pending team requests"
                        checked={(() => {
                          const currentReqs = (activeTab === 'leaves' ? leaveRequests : activeTab === 'wfh' ? wfhRequests : odRequests).filter(r => r.managerStatus === 'PENDING');
                          return currentReqs.length > 0 && currentReqs.every(r => selectedTeamReqIds.includes(r._id));
                        })()}
                        onChange={(e) => {
                          const currentReqs = (activeTab === 'leaves' ? leaveRequests : activeTab === 'wfh' ? wfhRequests : odRequests).filter(r => r.managerStatus === 'PENDING');
                          if (e.target.checked) {
                            setSelectedTeamReqIds(currentReqs.map(r => r._id));
                          } else {
                            setSelectedTeamReqIds([]);
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {activeTab === 'leaves' ? 'Type / Days' : 'Reason'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(activeTab === 'leaves' ? leaveRequests : activeTab === 'wfh' ? wfhRequests : odRequests).length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">
                        No {activeTab} requests from your team.
                      </td>
                    </tr>
                  ) : (
                    (activeTab === 'leaves' ? leaveRequests : activeTab === 'wfh' ? wfhRequests : odRequests).map((req) => {
                      const isPending = req.managerStatus === 'PENDING';
                      const isChecked = selectedTeamReqIds.includes(req._id);
                      return (
                      <tr key={req._id} className={`hover:bg-gray-50 transition-colors ${isChecked ? 'bg-blue-50/40' : ''}`}>
                        <td className="px-4 py-4 text-center">
                          {isPending ? (
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                setSelectedTeamReqIds(prev =>
                                  prev.includes(req._id) ? prev.filter(x => x !== req._id) : [...prev, req._id]
                                );
                              }}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                            />
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{req.employeeId.firstName} {req.employeeId.lastName}</div>
                          <div className="text-xs text-gray-500">{req.employeeId.employeeId}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {activeTab === 'od' ? new Date(req.date).toLocaleDateString() : (
                            <>
                              {new Date(req.startDate).toLocaleDateString()} - {new Date(req.endDate).toLocaleDateString()}
                            </>
                          )}
                        </td>
                      <td 
                        className="px-6 py-4 text-sm text-gray-900 max-w-xs cursor-pointer hover:text-blue-600 transition-colors"
                        title="Click to view full reason"
                        onClick={() => {
                          setSelectedRequest(req);
                          setDetailsModalOpen(true);
                        }}
                      >
                        {activeTab === 'leaves' ? (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-xs">{req.leaveType?.name}</span>
                            <span className="text-xs text-gray-500 mr-1">{req.days} Day(s)</span>
                            <span className="truncate max-w-[120px] text-gray-700">{req.reason}</span>
                            <span className="text-[10px] bg-blue-50 text-blue-600 font-semibold px-1.5 py-0.5 rounded shrink-0">View</span>
                          </div>
                        ) : (
                          <div>
                            {req.attendanceId && (
                              <span className="inline-block px-2 py-0.5 mb-1 text-[11px] font-semibold rounded bg-indigo-50 text-indigo-700 border border-indigo-200 mr-1.5">
                                💼 Meeting Clock-In
                              </span>
                            )}
                            <div className="flex items-center gap-1.5">
                              <span className="truncate">{req.reason}</span>
                              <span className="text-[10px] bg-blue-50 text-blue-600 font-semibold px-1.5 py-0.5 rounded shrink-0">View</span>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(req)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => {
                            setCurrentHistory(req.history || []);
                            setHistoryModalOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 mr-4 font-normal text-xs underline"
                        >
                          Track
                        </button>
                        {req.managerStatus === 'PENDING' ? (
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => handleUpdateStatus(activeTab, req._id, 'APPROVED')}
                              className="text-green-600 bg-green-50 hover:bg-green-100 px-3 py-1 rounded transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(activeTab, req._id, 'REJECTED')}
                              className="text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1 rounded transition-colors"
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                           <span className="text-gray-400 text-xs italic">Decided</span>
                        )}
                      </td>
                    </tr>
                    );
                  })
                )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {hybridModalOpen && selectedHybridMember && (
        <HybridScheduleModal
          isOpen={hybridModalOpen}
          employee={selectedHybridMember}
          onClose={() => {
            setHybridModalOpen(false);
            setSelectedHybridMember(null);
          }}
          onSaved={() => {
            fetchTeamData();
          }}
        />
      )}

      {bulkGrantModalOpen && (
        <BulkGrantModal
          isOpen={bulkGrantModalOpen}
          defaultType={bulkGrantType}
          preloadedEmployees={grantTargetEmployees || teamMembers}
          title="Allocate WFH / Leave (Team)"
          subtitle="Assign Full Day Paid WFH or Leave to your team members"
          badgeText="Manager Allocation"
          onClose={() => {
            setBulkGrantModalOpen(false);
            setGrantTargetEmployees(null);
          }}
          onSuccess={() => {
            fetchTeamData();
          }}
        />
      )}
    </div>
  );
};

export default TeamRequests;
